import React, { Fragment, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { toastr } from 'react-redux-toastr';
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  FormFeedback,
} from 'reactstrap';
import {
  postSendTicketVoidByEmailSMSToCustomer,
  voidTicket,
  voidTicketWithCallback,
  getTicketDeleteModal,
  executeTicketVoidAction,
  registerPinPadVoid,
} from '../../../../../actions/index';
import { postTotalPosVoid } from '../../../../../actions/totalpos/Totalpos';
import { POST } from '../../../../../config/permissions';
import { generatePostVoidTicket } from '../../../../../config/endpoints';
import { TICKET_PATH } from '../../../../../config/paths';
import withEndpointAuthorization from '../../../authorization/withEndPointAuthorization';
import ConfirmationModal from '../../../../common/modal/ConfirmationModal';
import SaveButton from '../../../../common/button/SaveButton';
import Select from '../../../../common/forms/select/Select';
import LinkToModuleResource from '../../../../common/resource/LinkToModuleResource';
import { enumToSelectOptions } from '../../../../../utils/enum';
import { VOID_REASONS } from '../../../../../config/constants';
import { numberFormatter } from '../../../../../utils/number';
import printTotalPosVoid from '../../../../../utils/printers/TotalPosVoid';

const VoidTicketButton = ({
  ticketId,
  customerOrderId,
  user: { salesSessionUserId, id: userId },
  email,
  className,
  disabled,
  afterVoidTicket,
  dispatchVoidTicket,
  dispatchVoidTicketWithCallback,
  dispatchExecuteTicketVoidAction,
  dispatchGetTicketDeleteModal,
  dispatchPostSendTicketVoidByEmailSMSToCustomer,
  dispatchPostTotalPosVoid,
  dispatchRegisterPinPadVoid,
}) => {
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [showJustificationModal, setShowJustificationModal] = useState(false);
  const [justification, setJustification] = useState('');
  const [validJustification, setValidJustification] = useState(true);
  const [checkingOrder, setCheckingOrder] = useState(false);
  const [showOrderDeleteModal, setShowOrderDeleteModal] = useState(false);
  const [orderDeleteMessage, setOrderDeleteMessage] = useState('');
  const [orderDeleteTicketList, setOrderDeleteTicketList] = useState([]);
  const [actionUrl, setActionUrl] = useState(null);
  const [actionMethod, setActionMethod] = useState(null);
  const [pinPadTransactionList, setPinPadTransactionList] = useState([]);
  const [pinPadProcessing, setPinPadProcessing] = useState({});
  // kept only so the cashier can (re)print the void voucher on demand - the
  // backend's pinPadTransactionList doesn't carry the printing fields, those
  // only exist in the terminal's own response
  const [pinPadVoidResponses, setPinPadVoidResponses] = useState({});

  const allPinPadTransactionsVoided = pinPadTransactionList.every(
    (transaction) => transaction.voided,
  );

  const refreshPinPadTransactionList = async () => {
    const refreshed = await dispatchGetTicketDeleteModal(ticketId);
    setActionUrl(refreshed ? refreshed.url : null);
    setActionMethod(refreshed ? refreshed.method : null);
    setPinPadTransactionList(
      refreshed && refreshed.pinPadTransactionList
        ? refreshed.pinPadTransactionList
        : [],
    );
  };

  const showJustificationModalWindow = () => {
    setJustification('');
    setShowWarningModal(false);
    setShowJustificationModal(true);
    setValidJustification(true);
  };

  const closeJustificationModal = () => setShowJustificationModal(false);

  // when the order was paid with a single payment method the backend skips
  // the confirmation step and returns an empty body
  const handleClickVoid = async () => {
    if (!customerOrderId) {
      setShowWarningModal(true);
      return;
    }

    setCheckingOrder(true);
    try {
      const response = await dispatchGetTicketDeleteModal(ticketId);

      setActionUrl(response ? response.url : null);
      setActionMethod(response ? response.method : null);
      setPinPadTransactionList(
        response && response.pinPadTransactionList
          ? response.pinPadTransactionList
          : [],
      );
      setPinPadProcessing({});
      setPinPadVoidResponses({});

      if (response && response.message) {
        setOrderDeleteMessage(response.message);
        setOrderDeleteTicketList(response.ticketList || []);
        setShowOrderDeleteModal(true);
      } else {
        showJustificationModalWindow();
      }
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      setCheckingOrder(false);
    }
  };

  const handleConfirmOrderDelete = () => {
    setShowOrderDeleteModal(false);
    showJustificationModalWindow();
  };

  const handleCancelOrderDelete = () => setShowOrderDeleteModal(false);

  // the charge is reversed on the pinpad first and registered with the
  // backend right away (rather than waiting for the final "Guardar"), so a
  // card is never left charged if the user closes the modal mid-flow - the
  // backend then blocks the final ticket void until every card is voided
  const handleVoidPinPadTransaction = async (transaction) => {
    setPinPadProcessing((prev) => ({ ...prev, [transaction.id]: true }));

    const response = await dispatchPostTotalPosVoid({
      financialReference: transaction.financialReference,
      operator: String(userId),
      amount: parseFloat(transaction.amount),
      qrPayment: transaction.paymentMethod === 'QR',
    });

    if (!response) {
      setPinPadProcessing((prev) => {
        const next = { ...prev };
        delete next[transaction.id];
        return next;
      });
      return;
    }

    setPinPadVoidResponses((prev) => ({ ...prev, [transaction.id]: response }));

    try {
      await dispatchRegisterPinPadVoid({
        ticketId,
        originalTransactionId: transaction.id,
        operation: 'VOID',
        approved: Boolean(response.approved),
        responseCode: response.responseCode,
        response,
      });
    } catch ({ message }) {
      toastr.error('Error', message);
    }

    await refreshPinPadTransactionList();
    setPinPadProcessing((prev) => {
      const next = { ...prev };
      delete next[transaction.id];
      return next;
    });
  };

  const handlePrintPinPadVoid = (transaction) => {
    const response = pinPadVoidResponses[transaction.id];
    if (!response) return;
    printTotalPosVoid(response, transaction.paymentMethod === 'QR');
  };

  const handleVoidTicket = () => {
    if (!justification.length) {
      setValidJustification(false);
      return;
    }

    if (!allPinPadTransactionsVoided) {
      return;
    }

    closeJustificationModal();
    if (salesSessionUserId) {
      dispatchPostSendTicketVoidByEmailSMSToCustomer({
        email,
        ticketId,
      });
    }

    if (customerOrderId && actionUrl) {
      const dispatched = dispatchExecuteTicketVoidAction({
        url: actionUrl,
        method: actionMethod,
        ticketId,
        justification,
      });
      if (afterVoidTicket) {
        dispatched.then(afterVoidTicket);
      }
      return;
    }

    if (afterVoidTicket) {
      dispatchVoidTicketWithCallback(
        { ticketId, justification },
        afterVoidTicket,
      );
    } else {
      dispatchVoidTicket({ ticketId, justification });
    }
  };

  const handleOnChangeJustification = (event) => {
    const { value } = event.target;

    setJustification(value);

    if (value && value.length) {
      setValidJustification(true);
    }
  };

  const handleOnChangeSelectJustification = (option) => {
    const { label } = option;

    setJustification(label);

    if (label && label.length) {
      setValidJustification(true);
    }
  };

  const warningModal = (
    <ConfirmationModal
      show={showWarningModal}
      title="Confirmación de Anulación"
      onClickConfirm={showJustificationModalWindow}
      onClickCancel={() => setShowWarningModal(false)}
    />
  );

  const orderDeleteModal = (
    <ConfirmationModal
      show={showOrderDeleteModal}
      title="Confirmación de Anulación"
      body={
        <>
          <p>{orderDeleteMessage}</p>
          <ul>
            {orderDeleteTicketList.map((orderTicket) => (
              <li key={orderTicket.id}>
                <LinkToModuleResource
                  text={`${orderTicket.documentSeries}-${orderTicket.documentCode}`}
                  href={`${TICKET_PATH}/${orderTicket.id}`}
                />
              </li>
            ))}
          </ul>
        </>
      }
      onClickConfirm={handleConfirmOrderDelete}
      onClickCancel={handleCancelOrderDelete}
    />
  );

  const justificationModal = (
    <Modal isOpen={showJustificationModal}>
      <ModalHeader>Razón de Anulación</ModalHeader>
      <ModalBody>
        {pinPadTransactionList.length > 0 && (
          <div className="mb-3">
            <p>
              Se debe anular el cobro en el POS de cada tarjeta antes de
              confirmar la anulación del boleto:
            </p>
            <ul className="list-unstyled">
              {pinPadTransactionList.map((transaction) => {
                const processing = Boolean(pinPadProcessing[transaction.id]);
                return (
                  <li key={transaction.id} className="mb-2">
                    {`${transaction.cardApplication || ''} ${
                      transaction.cardNumber || ''
                    } - ${numberFormatter({
                      style: 'currency',
                      value: transaction.amount,
                    })}`}{' '}
                    {transaction.voided ? (
                      <>
                        <span className="text-success">
                          <i className="fa fa-check" /> Anulado
                        </span>{' '}
                        {pinPadVoidResponses[transaction.id] && (
                          <Button
                            size="sm"
                            color="secondary"
                            onClick={() => handlePrintPinPadVoid(transaction)}
                          >
                            <i className="fa fa-print" /> Imprimir
                          </Button>
                        )}
                      </>
                    ) : (
                      <Button
                        size="sm"
                        color="danger"
                        disabled={processing}
                        onClick={() => handleVoidPinPadTransaction(transaction)}
                      >
                        <i
                          className={`fa fa-${
                            processing ? 'spinner fa-spin' : 'credit-card'
                          }`}
                        />{' '}
                        Anular en POS
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        {salesSessionUserId ? (
          <Select
            input={{ onChange: handleOnChangeSelectJustification }}
            options={enumToSelectOptions(VOID_REASONS)}
          />
        ) : (
          <>
            <Input
              type="textarea"
              invalid={!validJustification}
              name="justification"
              id="justification"
              placeholder="Ingrese razón de anulación"
              value={justification}
              onChange={handleOnChangeJustification}
            />
            <FormFeedback>
              Favor ingresar la razón de la anulación.
            </FormFeedback>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <SaveButton
          action={handleVoidTicket}
          disabled={!allPinPadTransactionsVoided}
        />{' '}
        <Button color="secondary" onClick={closeJustificationModal}>
          Cerrar
        </Button>
      </ModalFooter>
    </Modal>
  );

  return (
    <Fragment>
      <Button
        className={className}
        color="danger"
        onClick={handleClickVoid}
        disabled={disabled || checkingOrder}
      >
        <i className={`fa fa-${checkingOrder ? 'spinner fa-spin' : 'ban'}`} />{' '}
        Anular
      </Button>
      {warningModal}
      {orderDeleteModal}
      {justificationModal}
    </Fragment>
  );
};

VoidTicketButton.propTypes = {
  dispatchVoidTicket: PropTypes.func.isRequired,
  dispatchVoidTicketWithCallback: PropTypes.func.isRequired,
  dispatchExecuteTicketVoidAction: PropTypes.func.isRequired,
  dispatchGetTicketDeleteModal: PropTypes.func.isRequired,
  dispatchPostSendTicketVoidByEmailSMSToCustomer: PropTypes.func.isRequired,
  dispatchPostTotalPosVoid: PropTypes.func.isRequired,
  dispatchRegisterPinPadVoid: PropTypes.func.isRequired,
  ticketId: PropTypes.number.isRequired,
  customerOrderId: PropTypes.number,
  user: PropTypes.shape({
    id: PropTypes.number,
    username: PropTypes.string,
    salesSessionUserId: PropTypes.number,
  }).isRequired,
  email: PropTypes.string,
  className: PropTypes.string,
  afterVoidTicket: PropTypes.func,
  disabled: PropTypes.bool,
};

VoidTicketButton.defaultProps = {
  customerOrderId: null,
  email: '',
  className: '',
  afterVoidTicket: null,
  disabled: false,
};

const mapStateToProps = ({ authentication }) => ({
  user: authentication.get('user'),
});

const mapDispatchToProps = {
  dispatchVoidTicket: voidTicket,
  dispatchVoidTicketWithCallback: voidTicketWithCallback,
  dispatchExecuteTicketVoidAction: executeTicketVoidAction,
  dispatchGetTicketDeleteModal: getTicketDeleteModal,
  dispatchPostSendTicketVoidByEmailSMSToCustomer:
    postSendTicketVoidByEmailSMSToCustomer,
  dispatchPostTotalPosVoid: postTotalPosVoid,
  dispatchRegisterPinPadVoid: registerPinPadVoid,
};

const connectedComponent = connect(
  mapStateToProps,
  mapDispatchToProps,
)(VoidTicketButton);

export default withEndpointAuthorization({
  url: generatePostVoidTicket(0),
  permissionType: POST,
})(connectedComponent);
